/**
 * Typed errors for content-control creation (see createContentControlForCoObject in ContentControl.ts).
 * Intentionally minimal: exactly the two error classes needed by that function and its callers.
 */

/** Duck-typed guard for an OfficeExtension.Error-like cause, since the real class isn't available in tests. */
function isOfficeExtensionErrorLike(error: unknown): error is { code: string; debugInfo?: { errorLocation?: string } } {
  return typeof error === "object" && error !== null && "code" in error;
}

function resolveMessage(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message;
  }
  return String(cause);
}

/**
 * Thrown when creating (or configuring) the content control for a co-object fails, or when the
 * inserted content control never yields a usable id. Carries everything a caller needs to decide
 * whether an orphan content control still needs cleanup: `contentControlId` (when known) and
 * `orphanCleaned` (whether this function already attempted and succeeded at removing it).
 */
export class ContentControlCreationError extends Error {
  public readonly cause: unknown;
  public readonly code?: string;
  public readonly errorLocation?: string;
  public readonly contentControlId?: number;
  public readonly orphanCleaned: boolean;

  constructor(cause: unknown, contentControlId: number | undefined, orphanCleaned: boolean) {
    super(resolveMessage(cause));
    this.name = "ContentControlCreationError";
    this.cause = cause;
    this.contentControlId = contentControlId;
    this.orphanCleaned = orphanCleaned;

    if (isOfficeExtensionErrorLike(cause)) {
      this.code = cause.code;
      this.errorLocation = cause.debugInfo?.errorLocation;
    }

    Object.setPrototypeOf(this, ContentControlCreationError.prototype);
  }
}

/**
 * Thrown when the current selection is not a valid content-control insertion location (it spans
 * more than one table cell, or a whole row). Nothing is created in this case, so there is no id.
 */
export class InsertionLocationNotAllowedError extends Error {
  constructor(message = "Content control insertion is not allowed: the selection spans more than one table cell.") {
    super(message);
    this.name = "InsertionLocationNotAllowedError";
    Object.setPrototypeOf(this, InsertionLocationNotAllowedError.prototype);
  }
}
